import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { useVaultPerformance } from '@/app/components/VaultsItemPageComponents/hooks/useVaultPerformance';
import VaultsItemChartTooltip from '@/app/components/VaultsItemPageComponents/VaultsItemChartTooltip';
import { SERIES_COLORS, VAULT_TRANCHE } from '@/app/shared/constants/vaults';
import { formatAxisCompact } from '@/app/shared/lib/formatters';
import { getPerformanceScale } from '@/app/shared/lib/vaultPerformance';
import { PerformanceChartData, PerformanceChartPeriod } from '@/app/shared/ui/PerformanceChart/types';
import { Skeleton } from '@/app/shared/ui/Skeleton';
import { Tabs } from '@/app/shared/ui/Tabs';

interface VaultsItemPerformanceProps {
  vaultId?: string;
}

const METRIC_TABS = [
  { id: PerformanceChartData.APR, content: 'APR' },
  { id: PerformanceChartData.TVL, content: 'TVL' },
  { id: PerformanceChartData.SHARE_PRICE, content: 'Share Price' },
];

const PERIOD_TABS = [
  { id: PerformanceChartPeriod.WEEK, content: '7D' },
  { id: PerformanceChartPeriod.MONTH, content: '30D' },
  { id: PerformanceChartPeriod.QUARTER, content: '90D' },
  { id: PerformanceChartPeriod.ALL_TIME, content: 'All Time' },
];

const Y_AXIS_MIN_WIDTH = 40;
const Y_AXIS_LABEL_PADDING = 12;
const Y_AXIS_FONT_SIZE = 11;

const measureYAxisWidth = (labels: string[]): number => {
  if (typeof document === 'undefined' || labels.length === 0) return Y_AXIS_MIN_WIDTH;

  const fontFamily =
    getComputedStyle(document.documentElement).getPropertyValue('--font-geist').trim() ||
    'ui-sans-serif, system-ui, sans-serif';
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    const approx = Math.max(...labels.map((label) => label.length)) * (Y_AXIS_FONT_SIZE * 0.62);
    return Math.ceil(Math.max(Y_AXIS_MIN_WIDTH, approx + Y_AXIS_LABEL_PADDING));
  }

  ctx.font = `${Y_AXIS_FONT_SIZE}px ${fontFamily}`;
  const maxLabelWidth = labels.reduce((max, label) => Math.max(max, ctx.measureText(label).width), 0);
  return Math.ceil(Math.max(Y_AXIS_MIN_WIDTH, maxLabelWidth + Y_AXIS_LABEL_PADDING));
};

const VaultsItemPerformance = ({ vaultId }: VaultsItemPerformanceProps) => {
  const [metric, setMetric] = useState(PerformanceChartData.APR);
  const [period, setPeriod] = useState(PerformanceChartPeriod.WEEK);
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartSize, setChartSize] = useState({ width: 0, height: 0 });

  const { points, hasSenior, hasJunior, isLoading } = useVaultPerformance({
    vaultId,
    period,
    metric,
  });

  useLayoutEffect(() => {
    const el = chartRef.current;
    if (!el) return;

    const updateSize = () => {
      const { width, height } = el.getBoundingClientRect();
      setChartSize({ width: Math.floor(width), height: Math.floor(height) });
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const showBaseSeries = metric !== PerformanceChartData.SHARE_PRICE;

  const scale = useMemo(() => {
    const values = points.flatMap((point) => {
      const seriesValues = [point.senior, point.junior];
      if (showBaseSeries) seriesValues.unshift(point.base);
      return seriesValues.filter((value): value is number => value != null);
    });
    return getPerformanceScale(values, metric);
  }, [metric, points, showBaseSeries]);

  const yTicks = useMemo(() => {
    if (metric === PerformanceChartData.SHARE_PRICE || metric === PerformanceChartData.TVL) {
      return [0, 1, 2, 3, 4].map((index) => scale.min + ((scale.max - scale.min) * index) / 4);
    }

    const ticks: number[] = [];
    for (let value = scale.min; value <= scale.max + 0.001; value += 5) {
      ticks.push(value);
    }
    return ticks;
  }, [metric, scale.max, scale.min]);

  const formatY = (value: number) => {
    if (metric === PerformanceChartData.SHARE_PRICE) {
      return formatAxisCompact(value, '$', '', 2, 2);
    }
    if (metric === PerformanceChartData.TVL) {
      return formatAxisCompact(value, '$', '', 1, 1);
    }
    return formatAxisCompact(value, '', '%', 0, 0);
  };

  const yAxisWidth = useMemo(() => {
    const labels = yTicks.map((tick) => {
      if (metric === PerformanceChartData.SHARE_PRICE) {
        return formatAxisCompact(tick, '$', '', 2, 2);
      }
      if (metric === PerformanceChartData.TVL) {
        return formatAxisCompact(tick, '$', '', 1, 1);
      }
      return formatAxisCompact(tick, '', '%', 0, 0);
    });
    return measureYAxisWidth(labels);
  }, [metric, yTicks]);

  const hasChartSize = chartSize.width > 0 && chartSize.height > 0;
  const baseSeriesLabel = metric === PerformanceChartData.TVL ? 'Total TVL' : 'Base APR';

  return (
    <div className='flex h-full min-h-0 w-full min-w-0 flex-col gap-5 rounded-md bg-elevated-card-2 p-5'>
      <div className='flex flex-row items-center justify-between gap-5'>
        <p className='text-brand-1100'>Performance</p>
        <Tabs
          tabs={METRIC_TABS}
          activeTab={metric}
          onTabChange={setMetric}
          className='flex h-9.5 rounded-md bg-background p-0.5'
          tabClassName='flex h-full items-center rounded-sm px-3 text-base leading-5'
          activeTabClassName='bg-elevated-card-2 text-brand-1100'
          inactiveTabClassName='text-gray-400 hover:text-gray-50'
        />
      </div>

      <div className='flex h-7.5 items-center gap-x-4 md:justify-end'>
        {showBaseSeries && (
          <span className='flex items-center gap-2 text-base text-gray-400'>
            <span className='h-px w-4 border-t border-dashed border-chart-base' />
            {baseSeriesLabel}
          </span>
        )}
        {hasSenior && (
          <span className='flex items-center gap-2 text-base text-gray-400'>
            <span className='h-px w-4 bg-chart-senior' />
            Senior
          </span>
        )}
        {hasJunior && (
          <span className='flex items-center gap-2 text-base text-gray-400'>
            <span className='h-px w-4 bg-chart-junior' />
            Junior
          </span>
        )}
      </div>

      <div ref={chartRef} className='relative h-166.5 w-full min-w-0 shrink-0 md:h-auto md:min-h-55 md:flex-1 md:shrink'>
        <div className='absolute inset-0 min-h-0 min-w-0'>
          {isLoading && (
            <Skeleton.Line loading className='h-full w-full rounded-md'>
              <div className='h-full' />
            </Skeleton.Line>
          )}
          {!isLoading && hasChartSize && (
            <ResponsiveContainer width={chartSize.width} height={chartSize.height} minWidth={0}>
              <LineChart data={points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid horizontal vertical={false} stroke='var(--chart-grid)' strokeWidth={1} />
                <XAxis
                  dataKey='date'
                  tick={{ fill: 'var(--gray-500)', fontSize: 11, fontFamily: 'var(--font-geist)' }}
                  axisLine={false}
                  tickLine={false}
                  dy={10}
                />
                <YAxis
                  domain={[scale.min, scale.max]}
                  ticks={yTicks}
                  interval={0}
                  tick={{ fill: 'var(--gray-500)', fontSize: 11, fontFamily: 'var(--font-geist)' }}
                  axisLine={false}
                  tickLine={false}
                  width={yAxisWidth}
                  tickFormatter={formatY}
                />
                <Tooltip content={<VaultsItemChartTooltip metric={metric} />} />
                {showBaseSeries && (
                  <Line
                    type='linear'
                    dataKey='base'
                    name={baseSeriesLabel}
                    stroke={SERIES_COLORS.base}
                    strokeWidth={1}
                    strokeDasharray='5 6'
                    dot={false}
                    isAnimationActive={false}
                  />
                )}
                {hasSenior && (
                  <Line
                    type='linear'
                    dataKey={VAULT_TRANCHE.SENIOR}
                    name='Senior'
                    stroke={SERIES_COLORS[VAULT_TRANCHE.SENIOR]}
                    strokeWidth={1.25}
                    dot={false}
                    isAnimationActive={false}
                  />
                )}
                {hasJunior && (
                  <Line
                    type='linear'
                    dataKey={VAULT_TRANCHE.JUNIOR}
                    name='Junior'
                    stroke={SERIES_COLORS[VAULT_TRANCHE.JUNIOR]}
                    strokeWidth={1.25}
                    dot={false}
                    isAnimationActive={false}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <Tabs
        tabs={PERIOD_TABS}
        activeTab={period}
        onTabChange={setPeriod}
        className='flex h-7.5 items-center gap-5'
        tabClassName='text-base leading-5'
        activeTabClassName='text-brand-1100'
        inactiveTabClassName='text-gray-400 hover:text-gray-50'
      />
    </div>
  );
};

export default VaultsItemPerformance;
